/**
 * Which hue each lesson's glow takes.
 *
 * A small fixed set, assigned so that lessons sitting next to each other on
 * the map never share one: biggest lesson first, each takes the first hue
 * none of its already-coloured neighbours has. Neighbours are lessons whose
 * circles come within `reach` of each other. Where a neighbourhood has used
 * every hue, the least-used one among the neighbours is reused.
 *
 * Deterministic: the same layout gives the same colours on every visit. Pure,
 * so it is tested without a canvas.
 */

export interface HueCircle {
  subject: string;
  count: number;
  x: number;
  y: number;
  r: number;
}

export function assignLessonHues(
  clusters: readonly HueCircle[],
  hueCount: number,
  reach = 80
): Map<string, number> {
  const order = [...clusters].sort(
    (a, b) => b.count - a.count || a.subject.localeCompare(b.subject)
  );
  const hue = new Map<string, number>();
  for (const c of order) {
    const used = new Map<number, number>();
    for (const other of order) {
      const h = hue.get(other.subject);
      if (h == null) continue;
      const gap = Math.hypot(c.x - other.x, c.y - other.y) - c.r - other.r;
      if (gap <= reach) used.set(h, (used.get(h) ?? 0) + 1);
    }
    let pick = -1;
    for (let h = 0; h < hueCount; h++) {
      if (!used.has(h)) {
        pick = h;
        break;
      }
    }
    if (pick < 0) {
      pick = 0;
      for (let h = 1; h < hueCount; h++)
        if ((used.get(h) ?? 0) < (used.get(pick) ?? 0)) pick = h;
    }
    hue.set(c.subject, pick);
  }
  return hue;
}
